import { Link } from "react-router-dom";
import { ConnectionBrandIcon } from "@/components/ConnectionBrandIcon";
import { PublicDocLayout, PublicSectionAnchor } from "@/components/PublicDocLayout";
import { PublicLayout } from "@/components/PublicLayout";
import { PublicPageHero } from "@/components/PublicPageHero";
import { SUPPORT_EMAIL, SUPPORT_HOURS, SUPPORT_RESPONSE } from "@/lib/publicSite";

const GUIDE_TOC = [
  { id: "prerequisites", label: "Prerequisites" },
  { id: "add", label: "Adding the app" },
  { id: "use", label: "Using Hub with Teams" },
  { id: "remove", label: "Removing the app" },
  { id: "data", label: "Your data after removal" },
  { id: "troubleshooting", label: "Troubleshooting" },
  { id: "support", label: "Support" },
] as const;

export function DocsTeamsPage() {
  return (
    <PublicLayout title="ATW Hub for Microsoft Teams — Integration guide">
      <article className="public-doc">
        <PublicPageHero
          eyebrow="Guide"
          title="ATW Hub for Microsoft Teams"
          lede="How to add, use, and remove ATW Hub's Microsoft Teams chat integration."
          updated="October 1, 2026"
          marks={<ConnectionBrandIcon id="teams" size={40} />}
        />

        <PublicDocLayout toc={[...GUIDE_TOC]}>
          <section id="prerequisites" className="public-doc-section">
            <h2 className="public-section-title">
              <PublicSectionAnchor sectionId="prerequisites" />
              Prerequisites
            </h2>
            <ul className="public-list">
              <li>An ATW account with Hub enabled.</li>
              <li>
                A <strong>work or school</strong> Microsoft 365 account with Teams. Personal Microsoft
                accounts are not supported for Teams chat APIs.
              </li>
              <li>
                Your organization's admin may need to consent to ATW Hub's Microsoft app before you can
                connect, especially if the app is not yet publisher-verified in Microsoft Entra.
              </li>
            </ul>
          </section>

          <section id="add" className="public-doc-section">
            <h2 className="public-section-title">
              <PublicSectionAnchor sectionId="add" />
              Adding the app
            </h2>
            <ol className="public-steps">
              <li>Sign in to Hub at hub.atw.net.</li>
              <li>Open <strong>Settings</strong> → <strong>Connections</strong> → <strong>Chat</strong>.</li>
              <li>Click <strong>Connect Microsoft Teams</strong>.</li>
              <li>
                Sign in with your work or school account on Microsoft's consent screen. Review permissions
                (chat read/write for chats you can access) and click <strong>Accept</strong>.
              </li>
              <li>Return to Hub. Your Teams connection should appear under Chat.</li>
            </ol>
            <p>
              If your tenant blocks the app, ask your Microsoft 365 administrator to grant admin consent
              for ATW Hub in the Entra admin center.
            </p>
          </section>

          <section id="use" className="public-doc-section">
            <h2 className="public-section-title">
              <PublicSectionAnchor sectionId="use" />
              Using Hub with Teams
            </h2>
            <p>
              Open <strong>Chat</strong> in Hub and choose your Microsoft Teams connection. Hub lists Teams
              chats (1:1 and group chats). Team <em>channels</em> are not included in the first version.
            </p>
            <p>
              Open a chat to load messages live from Microsoft Graph. Hub does not store message text on
              disk. Send messages, and edit or delete your own messages when Graph allows. Hub stores read
              markers so it can show unread state.
            </p>
            <p>
              Hub calls Graph only when you use Chat or related features, with short in-memory caches (about
              60 seconds for conversation lists). Hub does not archive Teams messages.
            </p>
          </section>

          <section id="remove" className="public-doc-section">
            <h2 className="public-section-title">
              <PublicSectionAnchor sectionId="remove" />
              Removing the app
            </h2>
            <h3>Disconnect in Hub</h3>
            <p>
              Settings → Connections → Chat → <strong>Disconnect</strong> next to Microsoft Teams. Hub
              deletes stored tokens and read markers for that connection.
            </p>
            <h3>Revoke in Microsoft</h3>
            <ol className="public-steps">
              <li>
                Open{" "}
                <a href="https://myapps.microsoft.com/">myapps.microsoft.com</a> (or your organization's app
                portal).
              </li>
              <li>Find <strong>ATW Hub</strong> under applications you have granted access to.</li>
              <li>Remove or revoke access for the app.</li>
            </ol>
            <p>
              Your tenant admin can also remove enterprise consent for ATW Hub in the Microsoft Entra admin
              center.
            </p>
          </section>

          <section id="data" className="public-doc-section">
            <h2 className="public-section-title">
              <PublicSectionAnchor sectionId="data" />
              Your data after removal
            </h2>
            <p>
              Disconnecting removes Hub's OAuth tokens and read markers from Hub's API host. Hub does not
              retain Teams message text. Messages you sent from Hub remain in Teams. See the{" "}
              <Link to="/privacy">Privacy Policy</Link>.
            </p>
          </section>

          <section id="troubleshooting" className="public-doc-section">
            <h2 className="public-section-title">
              <PublicSectionAnchor sectionId="troubleshooting" />
              Troubleshooting
            </h2>
            <ul className="public-list">
              <li>
                <strong>Admin consent required:</strong> contact your IT admin to approve ATW Hub for your
                tenant.
              </li>
              <li>
                <strong>Throttling (HTTP 429):</strong> wait and retry; Hub backs off automatically.
              </li>
              <li>
                <strong>Personal account:</strong> use a work or school account licensed for Teams.
              </li>
            </ul>
          </section>

          <section id="support" className="public-doc-section">
            <h2 className="public-section-title">
              <PublicSectionAnchor sectionId="support" />
              Support
            </h2>
            <p>
              Email <a href={`mailto:${SUPPORT_EMAIL}`}>{SUPPORT_EMAIL}</a>. {SUPPORT_RESPONSE}{" "}
              {SUPPORT_HOURS}. <Link to="/support">hub.atw.net/support</Link>.
            </p>
          </section>
        </PublicDocLayout>
      </article>
    </PublicLayout>
  );
}
